/**
 * Centralized Earnings Calculator for Drivers and Fleet Admins.
 * Automatically enforces cancellation policy rules:
 * - Cancelled >= 72h before departure: 100% refund (0% retained for driver/fleet)
 * - Cancelled between 12h and 72h before departure: 50% refund (50% retained)
 * - Cancelled < 12h before departure: 0% refund (100% retained)
 */

export const getCancellationPolicyRefund = (booking) => {
    if (!booking) return { refundPercent: 0, retainedPercent: 100, refundAmount: 0 };

    const fullTripCost = parseFloat(booking.trip_cost || 0);

    // 1. Explicit refund amount / percentage recorded on booking
    if (booking.refundAmount !== undefined && booking.refundAmount !== null) {
        const amt = typeof booking.refundAmount === "number" ? booking.refundAmount : parseFloat(String(booking.refundAmount).replace(",", ".")) || 0;
        const pct = fullTripCost > 0 ? Math.round((amt / fullTripCost) * 100) : 0;
        return { refundPercent: pct, retainedPercent: Math.max(0, 100 - pct), refundAmount: amt };
    }
    if (booking.refundAmountTtc !== undefined && booking.refundAmountTtc !== null) {
        const amt = typeof booking.refundAmountTtc === "number" ? booking.refundAmountTtc : parseFloat(String(booking.refundAmountTtc).replace(",", ".")) || 0;
        const pct = fullTripCost > 0 ? Math.round((amt / fullTripCost) * 100) : 0;
        return { refundPercent: pct, retainedPercent: Math.max(0, 100 - pct), refundAmount: amt };
    }
    if (booking.payment?.refund?.amount !== undefined && booking.payment?.refund?.amount !== null) {
        const amt = typeof booking.payment.refund.amount === "number" ? booking.payment.refund.amount : parseFloat(String(booking.payment.refund.amount).replace(",", ".")) || 0;
        const pct = fullTripCost > 0 ? Math.round((amt / fullTripCost) * 100) : 0;
        return { refundPercent: pct, retainedPercent: Math.max(0, 100 - pct), refundAmount: amt };
    }
    if (booking.payment?.refundAmount !== undefined && booking.payment?.refundAmount !== null) {
        const amt = typeof booking.payment.refundAmount === "number" ? booking.payment.refundAmount : parseFloat(String(booking.payment.refundAmount).replace(",", ".")) || 0;
        const pct = fullTripCost > 0 ? Math.round((amt / fullTripCost) * 100) : 0;
        return { refundPercent: pct, retainedPercent: Math.max(0, 100 - pct), refundAmount: amt };
    }
    if (booking.cancellation_refund_percentage !== undefined || booking.refund_percentage !== undefined) {
        const pct = parseFloat(booking.cancellation_refund_percentage ?? booking.refund_percentage) || 0;
        const amt = Number(((fullTripCost * pct) / 100).toFixed(2));
        return { refundPercent: pct, retainedPercent: Math.max(0, 100 - pct), refundAmount: amt };
    }
    if (booking.cancel_refund_percent !== undefined || booking.refundPercent !== undefined) {
        const pct = parseFloat(booking.cancel_refund_percent ?? booking.refundPercent) || 0;
        const amt = Number(((fullTripCost * pct) / 100).toFixed(2));
        return { refundPercent: pct, retainedPercent: Math.max(0, 100 - pct), refundAmount: amt };
    }

    // 2. Automatic derivation based on cancellation policy timing
    const tripDateStr = booking.tripdate || booking.pickup_datetime || booking.bookingDate;
    const cancelDateStr = booking.cancelledAt || booking.cancellation_date || booking.payment?.refund?.refundedAt || booking.updatedAt || booking.createdAt;

    if (tripDateStr && cancelDateStr) {
        const tripTime = new Date(tripDateStr).getTime();
        const cancelTime = new Date(cancelDateStr).getTime();

        if (!isNaN(tripTime) && !isNaN(cancelTime)) {
            const diffHours = (tripTime - cancelTime) / (1000 * 60 * 60);

            if (diffHours >= 72) {
                // >= 72h: 100% refund (0% retained)
                return { refundPercent: 100, retainedPercent: 0, refundAmount: fullTripCost };
            } else if (diffHours >= 12) {
                // 12h <= diff < 72h: 50% refund (50% retained)
                const amt = Number((fullTripCost * 0.5).toFixed(2));
                return { refundPercent: 50, retainedPercent: 50, refundAmount: amt };
            } else {
                // < 12h: 0% refund (100% retained)
                return { refundPercent: 0, retainedPercent: 100, refundAmount: 0 };
            }
        }
    }

    // Default: no refund info -> full retention
    return { refundPercent: 0, retainedPercent: 100, refundAmount: 0 };
};

export const calculateEarnings = (booking) => {
    if (!booking) {
        return {
            tripCost: 0,
            fullTripCost: 0,
            refundAmount: 0,
            refundPercent: 0,
            retainedPercent: 100,
            isCancelled: false,
            convenienceFees: 0,
            commissionBase: 0,
            fleetCommission: 0,
            driverShare: 0
        };
    }

    // 1. Inputs Normalization (supports negotiated driver_trip_cost if subcontracted)
    const fullTripCost = parseFloat(
        (booking.driver_trip_cost !== undefined && booking.driver_trip_cost !== null && !isNaN(Number(booking.driver_trip_cost)))
            ? booking.driver_trip_cost
            : (booking.trip_cost || 0)
    );
    const convenienceFees = parseFloat(booking.convenience_fees || 0);
    
    // Check for cancellation and refund
    const bookingStatus = (booking.status || "").toUpperCase();
    const isCancelled = bookingStatus === "CANCELLED";

    let refundAmount = 0;
    let refundPercent = 0;
    let retainedPercent = 100;

    if (isCancelled) {
        const policy = getCancellationPolicyRefund(booking);
        refundAmount = policy.refundAmount;
        refundPercent = policy.refundPercent;
        retainedPercent = policy.retainedPercent;
    }

    // Effective revenue retained from this booking
    const effectiveTripCost = isCancelled ? Math.max(0, fullTripCost - refundAmount) : fullTripCost;
    
    // 2. Base Calculation (Revenue subject to commission)
    const commissionBase = Math.max(0, effectiveTripCost - convenienceFees);
    
    // 3. Commission Logic (Percentage or Flat)
    const commRate = parseFloat(booking.fleet_admin_comission || 0);
    const feeType = booking.fleet_admin_fee_type || 'percentage';
    
    let fleetCommission = 0;
    let driverShare = 0;

    if (feeType === 'flat') {
        fleetCommission = effectiveTripCost > 0 ? commRate : 0;
        driverShare = Math.max(0, commissionBase - fleetCommission);
    } else {
        // Handle percentage (default) - Calcul inverse: Part chauffeur = Base / (1 + Taux / 100)
        const hasReturnLeg = Boolean(
            (booking.returnTrip && (typeof booking.returnTrip === 'object' ? booking.returnTrip.enabled : Boolean(booking.returnTrip))) ||
            (booking.trip_details?.returnTrip && (typeof booking.trip_details.returnTrip === 'object' ? booking.trip_details.returnTrip.enabled : Boolean(booking.trip_details.returnTrip))) ||
            booking.tripType === 'round_trip' ||
            booking.instructionData?.tripType === 'round_trip' ||
            booking.hasReturn
        );

        if (commRate > 0) {
            if (hasReturnLeg) {
                // Pour un aller-retour avec 2 trajets égaux, arrondir chaque trajet séparément
                const halfBase = commissionBase / 2;
                const legShare = Math.round((halfBase / (1 + (commRate / 100))) * 100) / 100;
                driverShare = legShare * 2;
                fleetCommission = Math.max(0, commissionBase - driverShare);
            } else {
                driverShare = Math.round((commissionBase / (1 + (commRate / 100))) * 100) / 100;
                fleetCommission = Math.max(0, commissionBase - driverShare);
            }
        } else {
            driverShare = commissionBase;
            fleetCommission = 0;
        }
    }

    return {
        tripCost: parseFloat(effectiveTripCost.toFixed(2)),
        fullTripCost: parseFloat(fullTripCost.toFixed(2)),
        refundAmount: parseFloat(refundAmount.toFixed(2)),
        refundPercent: refundPercent,
        retainedPercent: retainedPercent,
        isCancelled: isCancelled,
        convenienceFees: convenienceFees,
        commissionBase: parseFloat(commissionBase.toFixed(2)),
        fleetCommission: parseFloat(fleetCommission.toFixed(2)),
        driverShare: parseFloat(driverShare.toFixed(2))
    };
};
